import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState
} from 'react';

import { List, Undo2 } from 'lucide-react';

import { Button } from '@/shadcn/button';
import { cn } from '@/shadcn/lib/utils';
import { Textarea } from '@/shadcn/textarea';

import { useBeforeLeavingAlert } from '@/components/beforeLeavingAlert';
import Table from '@/components/ui/table';
import type { TableRow, TableUpdate } from '@/components/ui/table/table.type';
import {
  DATA_EDITOR_BATCH_UPDATE,
  DATA_EDITOR_SET,
  DATA_EDITOR_UNSET
} from '@/constant/dataEditor';
import { deepCopy, formatNumber, getByPath } from '@/utils';
import { BatchEditModal } from './batchEditModal/batchEditModal';
import type { DataEditorConfig } from './dataEditor.type';
import { applyModifiedDataChange } from './reducers';

type Props = {
  data: TableRow[];
  config: DataEditorConfig;
  onSubmit: (formData: unknown, cleanupLocalStorage: () => void) => void;
  submitLabel?: string;
  submitDisabled?: boolean;
  onCancel?: () => void;
  cancelLabel?: string;
  cachedUpdatesKey?: string;
  traceInfoViewer?: {
    showTraceInfoTray: boolean;
    toggleShowTraceInfoTray: () => void;
  };
};

export const DataEditor = memo(
  ({
    data,
    config,
    onSubmit,
    submitLabel = 'Submit Changes',
    submitDisabled = false,
    onCancel = () => {},
    cancelLabel = 'Cancel',
    cachedUpdatesKey,
    traceInfoViewer
  }: Props) => {
    const {
      modifiedDataKey = 'changes',
      addInitialData,
      initialDataKey = 'rows',
      addComments,
      commentsKey = 'comments',
      dataPath,
      table
    } = config;
    const { setAskBeforeLeaving, setLeavingCallback } = useBeforeLeavingAlert();

    const tableData = getByPath(data, dataPath) || [];

    const dataSelectorPath = table.dataSelectorPath || 'rowId';

    // data editor state
    // bind reducer with some props
    const modifiedDataReducer = useCallback(
      (currentModifiedData, action) =>
        applyModifiedDataChange(
          tableData,
          dataSelectorPath,
          cachedUpdatesKey,
          currentModifiedData,
          action
        ),
      [tableData, dataSelectorPath, cachedUpdatesKey]
    );
    const [modifiedData, onDataModified] = useReducer(modifiedDataReducer, {});

    // If modifiedData object contains modified lines, the user will be asked for confirmation before leaving the page or changing route.
    // If modifiedData is cleared (empty object), the user will not be asked for confirmation.
    useEffect(() => {
      const askBeforeLeaving =
        typeof modifiedData === 'object' &&
        Object.keys(modifiedData).length > 0;
      setAskBeforeLeaving(askBeforeLeaving);
    }, [modifiedData]);

    const [summaryData, setSummaryData] = useState<Record<
      string,
      unknown
    > | null>(null);

    // comments state
    const [commentsInputFocused, setCommentsInputFocused] =
      useState<boolean>(false);
    const [comments, setComments] = useState<string>('');

    // batch edit modal state
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [showBatchModal, setShowBatchModal] = useState<boolean>(false);
    const [batchUpdateCache, setBatchUpdateCache] = useState<{
      cache: Record<string, unknown>;
      nbRows: number;
    } | null>(null);
    const toggleBatchModal = useCallback(
      () => setShowBatchModal(!showBatchModal),
      [showBatchModal]
    );

    // at first mount, eventually get the data updates cached in local storage
    useEffect(() => {
      if (cachedUpdatesKey) {
        const localStorageModifiedDataStr =
          localStorage.getItem(cachedUpdatesKey);
        if (localStorageModifiedDataStr) {
          const localStorageModifiedData = JSON.parse(
            localStorageModifiedDataStr
          );
          onDataModified({
            type: DATA_EDITOR_SET,
            modifiedData: localStorageModifiedData,
            disableCache: true // do not cache what we have just read...
          });
        }
        const localStorageCommentsStr = localStorage.getItem(
          `${cachedUpdatesKey}_comments`
        );
        if (localStorageCommentsStr) {
          setComments(localStorageCommentsStr);
        }
      }
    }, [cachedUpdatesKey]);

    // this memo is passed to the table as the 'update' context
    // it holds all the patched data
    // and provides the update function only if the editor is not in 'summary' view
    const tableUpdate: TableUpdate = useMemo(
      () => ({
        userDisplay: {
          localStorageKey: `${cachedUpdatesKey}_tableConfig`
        },
        setSelectedRows,
        edit: {
          modifiedData,
          onDataModified: summaryData ? null : onDataModified,
          onClickBatchEdit: summaryData ? null : toggleBatchModal,
          batchEditClicked: showBatchModal,
          allowDisplayDiffs: true
        }
      }),
      [
        cachedUpdatesKey,
        modifiedData,
        summaryData,
        showBatchModal,
        toggleBatchModal
      ]
    );

    // callback to clear all patches
    const clearModifiedData = useCallback(() => {
      onDataModified({
        type: DATA_EDITOR_UNSET
      });
    }, []);

    const clearSummaryData = useCallback(() => {
      setSummaryData(null);
    }, []);

    const buildSummaryData = useCallback(() => {
      const builtSummaryData = tableData.filter(rowData => {
        // get the value of the row selector
        const rowSelector = getByPath(rowData, dataSelectorPath);
        // check it is in the modified data map
        return !!modifiedData[rowSelector];
      });
      setSummaryData(builtSummaryData);
    }, [tableData, modifiedData, dataSelectorPath]);

    // handle comments state
    const focusComments = useCallback(() => {
      setCommentsInputFocused(true);
    }, []);

    const blurComments = useCallback(() => {
      setCommentsInputFocused(false);
    }, []);

    const updateComments = useCallback(
      e => {
        const newComment = e.target.value;
        setComments(newComment);
        if (cachedUpdatesKey) {
          if (!newComment) {
            localStorage.removeItem(`${cachedUpdatesKey}_comments`);
            return;
          }
          localStorage.setItem(`${cachedUpdatesKey}_comments`, newComment);
        }
      },
      [cachedUpdatesKey]
    );

    // if submit is successful, dataEditor will cleanup all related localStorage items
    const cleanupLocalStorage = useCallback(() => {
      if (cachedUpdatesKey) {
        localStorage.removeItem(cachedUpdatesKey);
        localStorage.removeItem(`${cachedUpdatesKey}_comments`);
        localStorage.removeItem(`${cachedUpdatesKey}_tableConfig`);
      }
    }, [cachedUpdatesKey]);

    // Update leaving alert callback
    useEffect(() => {
      setLeavingCallback(cleanupLocalStorage);
    }, [setLeavingCallback, cleanupLocalStorage]);

    // submit the data
    const submitData = useCallback(() => {
      const formData = {
        [modifiedDataKey]: modifiedData
      };
      if (comments) formData[commentsKey] = comments;
      if (addInitialData) formData[initialDataKey] = summaryData;
      onSubmit(formData, cleanupLocalStorage);
    }, [
      modifiedDataKey,
      modifiedData,
      summaryData,
      comments,
      commentsKey,
      addInitialData,
      initialDataKey,
      onSubmit,
      cleanupLocalStorage
    ]);

    // submit a batch patch when closing the batch edit modal
    const submitBatchPatch = useCallback(
      (patch, editablePaths) => {
        // close the modal
        toggleBatchModal();

        // keep track of modifiedData just before batch edit
        setBatchUpdateCache({
          cache: deepCopy(modifiedData),
          nbRows: selectedRows.length
        });

        // update the batch
        onDataModified({
          type: DATA_EDITOR_BATCH_UPDATE,
          rowSelectors: selectedRows,
          patch,
          editablePaths
        });

        // set the timeout to clear the cache
        const batchUpdateTimeout = setTimeout(() => {
          setBatchUpdateCache(null);
        }, 5000);

        // clear the timeout at unmount
        return () => {
          clearTimeout(batchUpdateTimeout);
        };
      },
      [selectedRows, toggleBatchModal]
    );
    const undoBatchUpdate = useCallback(() => {
      onDataModified({
        type: DATA_EDITOR_SET,
        modifiedData: batchUpdateCache?.cache ?? undefined
      });
      setBatchUpdateCache(null);
    }, [batchUpdateCache]);

    // temp message to indicate that a batch edit has just been applied
    // with option to undo
    const batchUpdateMessage = batchUpdateCache && (
      <div className="borber absolute top-0 z-5 my-5 flex h-15 w-full max-w-[750px] items-center justify-center whitespace-pre rounded-md border-border bg-secondary px-5 py-2.5 text-base text-secondary-foreground">
        <b>{`${formatNumber(batchUpdateCache.nbRows)} row${
          batchUpdateCache.nbRows > 1 ? 's' : ''
        }`}</b>
        {` ${batchUpdateCache.nbRows > 1 ? 'have' : 'has'} been edited`}
        <Button
          className="absolute top-4 right-5 flex items-center gap-2 bg-card text-card-foreground"
          data-cy="undo-batch"
          onClick={undoBatchUpdate}
          variant="ghost"
        >
          <Undo2 className="mb-1" /> Undo
        </Button>
      </div>
    );

    const nbRowsModified = Object.keys(modifiedData).length;

    // base data editor (before confirmation step)
    if (!summaryData) {
      return (
        <>
          <div className="relative flex h-full w-full flex-col flex-nowrap items-center">
            {batchUpdateMessage}
            <div className="flex w-full grow flex-col flex-nowrap p-5">
              <Table config={table} data={tableData} update={tableUpdate} />
            </div>
          </div>
          <div className="z-2 flex w-full shrink-0 flex-row flex-nowrap items-end justify-between border-border border-t bg-card px-5 py-3 text-sm">
            <div className="flex h-full grow flex-row flex-nowrap items-center gap-4">
              <Button
                data-cy="cancel"
                disabled={false}
                onClick={onCancel}
                variant="outline"
              >
                {cancelLabel}
              </Button>
              {!!nbRowsModified && (
                <Button
                  data-cy="undo"
                  disabled={false}
                  onClick={clearModifiedData}
                  variant="ghostDestructive"
                >
                  undo all
                </Button>
              )}
              {traceInfoViewer && (
                <Button
                  active={traceInfoViewer.showTraceInfoTray}
                  data-cy="toggle-trace-info"
                  onClick={traceInfoViewer.toggleShowTraceInfoTray}
                  variant="pushtext"
                >
                  <List size={20} /> Show Trace Info
                </Button>
              )}
            </div>
            <div className="flex grow flex-row flex-nowrap items-center justify-end gap-4">
              <p className="text-muted-foreground">
                {`${nbRowsModified} rows changed`}
              </p>
              <Button
                data-cy="confirm-changes"
                disabled={!nbRowsModified}
                onClick={buildSummaryData}
              >
                confirm changes
              </Button>
            </div>
          </div>

          <BatchEditModal
            columns={table.columns}
            onOpenChange={setShowBatchModal}
            onSubmit={submitBatchPatch}
            open={showBatchModal}
            selectedRows={selectedRows}
          />
        </>
      );
    }

    // summary data editor (confirmation step)
    return (
      <>
        <div className="relative flex h-full w-full flex-col flex-nowrap items-center">
          <div className="flex w-full flex-row flex-nowrap items-center justify-between p-5 text-sm">
            <Button
              data-cy="go-back"
              data-label="Go Back"
              onClick={clearSummaryData}
              type="button"
              variant="outline"
            >
              Go Back
            </Button>
            <div className="flex flex-row flex-nowrap items-center">
              <div className="px-4 text-muted-foreground text-xs">{`${formatNumber(
                summaryData.length
              )} of ${formatNumber(
                tableData.length
              )} rows have been changed`}</div>
              <Button
                aria-label={submitLabel}
                data-cy="submit-button"
                disabled={submitDisabled}
                onClick={submitData}
                type="submit"
              >
                {submitLabel}
              </Button>
            </div>
          </div>
          <div className="flex w-full grow flex-col flex-nowrap p-5">
            <Table config={table} data={summaryData} update={tableUpdate} />
          </div>
        </div>
        <div className="z-2 flex w-full shrink-0 flex-row flex-nowrap items-end justify-between border-border border-t bg-card px-5 py-3 text-sm">
          <div className="flex h-full grow flex-row flex-nowrap items-center gap-4">
            {traceInfoViewer ? (
              <Button
                active={traceInfoViewer.showTraceInfoTray}
                data-cy="toggle-trace-info"
                onClick={traceInfoViewer.toggleShowTraceInfoTray}
                variant="pushtext"
              >
                <List size={20} /> Show Trace Info
              </Button>
            ) : (
              <div />
            )}
          </div>
          {addComments && (
            <div className="relative w-full max-w-[500px]">
              <Textarea
                className={cn(
                  'h-[5rem] max-h-[250px] min-h-[2.5rem] bg-card transition-all duration-100 ease-out',
                  !commentsInputFocused && 'h-[2.5rem]'
                )}
                data-cy="data-editor-comment"
                onBlur={blurComments}
                onChange={updateComments}
                onFocus={focusComments}
                placeholder="Add a comment"
              />
            </div>
          )}
          <div className="flex grow flex-row flex-nowrap items-center justify-end gap-4">
            <Button
              data-cy="submit-button"
              disabled={submitDisabled}
              onClick={submitData}
            >
              {submitLabel}
            </Button>
          </div>
        </div>
      </>
    );
  }
);
